<script setup lang="ts">
import { nextTick, computed, ref, type ComputedRef, onMounted } from 'vue'
import { useTextareaAutosize, computedWithControl, useManualRefHistory, watchDebounced } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useContext } from './context'

import Button from '~/ui/Button.vue'
import Spacer from '~/ui/Spacer.vue'
import Markdown from '~/ui/Markdown.vue'
import Label from '~/ui/Label.vue'
import Toolbar from '~/ui/Toolbar.vue'

import { type WithAttributes, toHtml, fw } from './attributes'

defineOptions({
  /* No attrs for label */
  inheritAttrs: false
})

// #region props
const {
  charLimit = Infinity,
  placeholder = '',
  initialLines = 3,
  maxBlockSize,
  uiSunken = useContext().value.uiSunken ?? undefined,
  ...props
} = defineProps<WithAttributes<{
  class: {
    justify?: 'justify-start'
  }
  label?: string
  placeholder?: string
  charLimit?: number
  initialLines?: number | string
  /** Caps the textarea's rendered height, scrolling internally instead of growing forever. The toolbar stays outside this area. */
  maxBlockSize?: string
  /** Text shown in the markdown preview instead of the raw editable value, e.g. with template tokens substituted. Editing always acts on the raw value. */
  previewText?: string
  autofocus?: boolean
  required?: boolean
  disabled?: boolean
  uiSunken?: boolean
}>>()
// #endregion props

const { t } = useI18n()

// #region model
const model = defineModel<string>({ required: true })
// #endregion model

const { undo, redo, commit: commitHistory, last } = useManualRefHistory(model)
const textarea = ref()
const { triggerResize } = useTextareaAutosize({ input: model, element: textarea })

const commit = () => {
  triggerResize()
  commitHistory()
}

const preview = ref(false)

watchDebounced(model, (value) => {
  if (value !== last.value.snapshot) {
    commit()
  }
}, { debounce: 300 })

const lineNumber = computedWithControl(
  () => [textarea.value, model],
  () => {
    const { selectionStart } = textarea.value ?? {}
    return model.value.slice(0, selectionStart).split('\n').length - 1
  }
)

const updateLineNumber = () => setTimeout(lineNumber.trigger, 0)

const currentLine = computed({
  get: () => (model.value.split('\n')[model.value.split('\n').length > lineNumber.value ? lineNumber.value : 0]),
  set: (line) => {
    const content = model.value.split('\n')
    content[lineNumber.value] = line!
    model.value = content.join('\n')
  }
})

// Textarea manipulation
const splice = async (start: number, deleteCount: number, items?: string) => {
  let { selectionStart, selectionEnd } = textarea.value

  const lineBeginning = model.value.slice(0, selectionStart).lastIndexOf('\n') + 1
  let lineStart = selectionStart - lineBeginning
  let lineEnd = selectionEnd - lineBeginning

  const text = currentLine.value!.split('')
  text.splice(start, deleteCount, items ?? '')
  currentLine.value = text.join('')

  if (start <= lineStart) {
    lineStart += items?.length ?? 0
    lineStart -= deleteCount
  }

  if (start <= lineEnd) {
    lineEnd += items?.length ?? 0
    lineEnd -= deleteCount
  }

  selectionStart = lineBeginning + Math.max(0, lineStart)
  selectionEnd = lineBeginning + Math.max(0, lineEnd)

  textarea.value.focus()
  await nextTick()
  textarea.value.setSelectionRange(selectionStart, selectionEnd)
}

const newLineOperations = new Map<RegExp, ((event: KeyboardEvent, line: string, groups: string[]) => void)>()
const newline = async (event: KeyboardEvent) => {
  const line = currentLine.value!
  for (const regexp of newLineOperations.keys()) {
    const matches = line.match(regexp) ?? []
    if (matches.length > 0) {
      newLineOperations.get(regexp)?.(event, line, matches.slice(1))
    }
  }
}

// Conditions
const isHeading1 = computed(() => currentLine.value!.startsWith('# '))
const isHeading2 = computed(() => currentLine.value!.startsWith('## '))
const isQuote = computed(() => currentLine.value!.startsWith('> '))
const isUnorderedList = computed(() => currentLine.value!.startsWith('- ') || currentLine.value!.startsWith('* '))
const isOrderedList = computed(() => /^\d+\. /.test(currentLine.value!))

const isParagraph = computed(() => !isHeading1.value && !isHeading2.value && !isQuote.value && !isUnorderedList.value && !isOrderedList.value)

// Prefix operations
const paragraph = async (shouldCommit = true) => {
  if (isHeading1.value || isQuote.value || isUnorderedList.value) {
    await splice(0, 2)
    if (shouldCommit) commit()
    return
  }

  if (isHeading2.value || isOrderedList.value) {
    await splice(0, 3)
    if (shouldCommit) commit()
  }
}

const prefixOperation = (prefix: string, condition?: ComputedRef<boolean>) => async () => {
  if (condition?.value) {
    return paragraph()
  }

  await paragraph(false)
  await splice(0, 0, prefix)
  return commit()
}

const heading1 = prefixOperation('# ', isHeading1)
const heading2 = prefixOperation('## ', isHeading2)
const quote = prefixOperation('> ', isQuote)
const orderedList = prefixOperation('1. ', isOrderedList)
const unorderedList = prefixOperation('- ', isUnorderedList)

// Newline operations
const newlineOperation = (regexp: RegExp, newLineHandler: (line: string, groups: string[]) => Promise<void> | void) => {
  newLineOperations.set(regexp, async (event, line, groups) => {
    event.preventDefault()

    if (new RegExp(regexp.toString().slice(1, -1) + '$').test(line)) {
      return paragraph()
    }

    await newLineHandler(line, groups)
    lineNumber.trigger()
    return commit()
  })
}

newlineOperation(/^(\d+)\. /, (line, [lastNumber]) => splice(line.length, 0, `\n${+lastNumber! + 1}. `))
newlineOperation(/^- /, line => splice(line.length, 0, '\n- '))
newlineOperation(/^> /, line => splice(line.length, 0, '\n> '))
newlineOperation(/^\* /, line => splice(line.length, 0, '\n* '))

// Inline operations
const inlineOperation = (chars: string) => async () => {
  const { selectionStart, selectionEnd } = textarea.value
  const lineBeginning = model.value.slice(0, selectionStart).lastIndexOf('\n') + 1
  await splice(selectionStart - lineBeginning, 0, chars)
  await splice(selectionEnd - lineBeginning + chars.length, 0, chars)

  const start = selectionStart === selectionEnd
    ? selectionStart + chars.length
    : selectionEnd + chars.length * 2
  textarea.value.setSelectionRange(start, start)
  return commit()
}

const bold = inlineOperation('**')
const italics = inlineOperation('_')
const strikethrough = inlineOperation('~~')

const link = async () => {
  const { selectionStart, selectionEnd } = textarea.value
  const lineBeginning = model.value.slice(0, selectionStart).lastIndexOf('\n') + 1
  await splice(selectionStart - lineBeginning, 0, '[')
  await splice(selectionEnd - lineBeginning + 1, 0, '](url)')
  textarea.value.setSelectionRange(selectionEnd + 3, selectionEnd + 6)
  return commit()
}

// Fix focus
const focus = () => textarea.value.focus()
onMounted(() => {
  if (props.autofocus) focus()
})
</script>

<template>
  <Label
    :label="props.label || undefined"
    :required
    :disabled
  >
    <template
      v-if="$slots.label"
      #label
    >
      <slot name="label" />
    </template>

    <div
      v-bind="$attrs"
      :class="[
        props.class,
        !props.class?.includes('color') && 'color-default-solid',
        $style.textareaContainer,
        { [$style.hasPreview]: preview },
      ]"
      :ui-sunken
      @mousedown.prevent="focus"
      @mouseup.prevent="focus"
    >
      <Markdown
        :md="previewText ?? model"
        class="color-default-solid" :class="[$style.preview]"
      />
      <textarea
        v-bind="toHtml<'textarea'>(props)"
        ref="textarea"
        v-model="model"
        :class="[
          fw.font.monospace,
          $style.textarea,
        ]"
        :maxlength="charLimit"
        :autofocus="autofocus || undefined"
        :required="required"
        :disabled="disabled"
        :placeholder="placeholder"
        :rows="initialLines"
        :style="{
          minBlockSize: `${((typeof (initialLines) === 'string' ? Number.parseInt(initialLines) : (initialLines ?? 3)) + 1.2) * 1.5}rem`,
          maxBlockSize,
          overflowBlock: 'auto',
        }"
        @click="updateLineNumber"
        @mousedown.stop
        @mouseup.stop
        @keydown.left="updateLineNumber"
        @keydown.right="updateLineNumber"
        @keydown.up="updateLineNumber"
        @keydown.down="updateLineNumber"
        @keydown.enter="newline"
        @keydown.ctrl.shift.z.exact.prevent="redo"
        @keydown.ctrl.z.exact.prevent="undo"
        @keydown.ctrl.b.exact.prevent="bold"
        @keydown.ctrl.i.exact.prevent="italics"
        @keydown.ctrl.shift.x.exact.prevent="strikethrough"
        @keydown.ctrl.k.exact.prevent="link"
      />

      <Toolbar>
        <span :class="$style.toolbar">
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.paragraph')"
            :aria-pressed="isParagraph ? 'true' : 'false'"
            :disabled="preview"
            @click="paragraph(true)"
          >
            <Icon icon="bi:paragraph" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.heading1')"
            :aria-pressed="isHeading1 ? 'true' : 'false'"
            :disabled="preview"
            @click="heading1"
          >
            <Icon icon="bi:type-h1" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.heading2')"
            :aria-pressed="isHeading2 ? 'true' : 'false'"
            :disabled="preview"
            @click="heading2"
          >
            <Icon icon="bi:type-h2" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.quote')"
            :aria-pressed="isQuote ? 'true' : 'false'"
            :disabled="preview"
            @click="quote"
          >
            <Icon icon="bi:quote" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.orderedList')"
            :aria-pressed="isOrderedList ? 'true' : 'false'"
            :disabled="preview"
            @click="orderedList"
          >
            <Icon icon="bi:list-ol" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.unorderedList')"
            :aria-pressed="isUnorderedList ? 'true' : 'false'"
            :disabled="preview"
            @click="unorderedList"
          >
            <Icon icon="bi:list-ul" />
          </Button>
        </span>

        <Spacer size-12 />

        <span :class="$style.toolbar">
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.bold')"
            :disabled="preview"
            @click="bold"
          >
            <Icon icon="bi:type-bold" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.italic')"
            :disabled="preview"
            @click="italics"
          >
            <Icon icon="bi:type-italic" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.strikethrough')"
            :disabled="preview"
            @click="strikethrough"
          >
            <Icon icon="bi:type-strikethrough" />
          </Button>
          <Button
            class="color-default-ghost shape-square"
            :aria-label="t('vui.aria.text.link')"
            :disabled="preview"
            @click="link"
          >
            <Icon icon="bi:link-45deg" />
          </Button>
        </span>

        <Spacer
          v-if="!$slots.default"
          grow
          size-12
        />

        <slot />

        <Spacer size-12 />

        <span
          v-if="charLimit !== Infinity && typeof charLimit === 'number'"
          :class="[fw.font.caption, $style.letterCount]"
        >
          {{ charLimit - model.length }}
        </span>

        <Spacer size-12 />

        <span :class="$style.toolbar">
          <Button
            class="color-default-ghost shape-field-minimal"
            :aria-pressed="preview ? 'true' : 'false'"
            @click="preview = !preview"
          >
            <Icon icon="bi:eye" />
            {{ t('vui.preview') }}
          </Button>
        </span>
      </Toolbar>
    </div>
  </Label>
</template>

<style module>
.textareaContainer {
  position: relative;
  padding: 8px;
  border-radius: 8px;
  overflow: hidden;
}

.hasPreview,
.textareaContainer:focus-within {
  .toolbar {
    opacity: 1;
    transform: translateY(0rem);
    pointer-events: auto;
  }
}

.hasPreview .preview {
  opacity: 1;
  transform: translateY(0rem);
  pointer-events: auto;
}

.preview {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  bottom: 42px;
  overflow-y: auto;
  opacity: 0;
  transform: translateY(0.5rem);
  pointer-events: none;
  transition: all 0.2s ease;

  >:first-child {
    margin-top: 12px;
  }
}

.textarea {
  resize: none;
  outline: none;
  border: none;
  display: block;
  width: 100%;
  padding: 8px 12px 8px;
}

.toolbar {
  display: flex;
  align-items: center;
  position: relative;
  opacity: 0;
  transform: translateY(1rem) scale(1.03);
  pointer-events: none;
  transition: all 0.2s ease;
  flex-wrap: wrap;
  margin-block-end: -8px;
  margin-inline: -8px;
}

.letterCount {
  opacity: 0.7;
}
</style>
